import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DollarOutlined } from '@ant-design/icons';
import { Button, Select, message } from 'antd';
import {
  getSalaryViewers,
  setSalaryViewers,
} from '@/services/compensation.service';

const sameSet = (a = [], b = []) =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

/**
 * "Who sees salaries" on the company's People and access tile: people of the
 * company, picked by name whatever their role, who see every employee's salary
 * (read-only). Saved on its own, apart from the company form. HLB only — for
 * anyone else the server refuses and the card is not shown.
 */
export default function SalaryViewersCard() {
  const { t } = useTranslation('translations');
  const tc = (key, opts) => t(`compensation.${key}`, opts);
  const { companyId } = useParams();
  const queryClient = useQueryClient();
  const queryKey = ['salary-viewers', companyId];

  const { data, isError, isLoading } = useQuery({
    queryKey,
    queryFn: () => getSalaryViewers(companyId),
    enabled: !!companyId,
    retry: false,
  });
  const [picked, setPicked] = useState([]);
  useEffect(() => {
    if (data?.viewers) setPicked(data.viewers);
  }, [data?.viewers]);

  const options = useMemo(
    () =>
      (data?.candidates || []).map((p) => ({
        value: String(p._id),
        label: p.email && p.email !== p.name ? `${p.name} (${p.email})` : p.name,
      })),
    [data?.candidates]
  );

  const save = useMutation({
    mutationFn: () => setSalaryViewers(companyId, picked),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKey, next);
      queryClient.invalidateQueries({ queryKey: ['compensation-companies'] });
      message.success(tc('saved'));
    },
    onError: (error) =>
      message.error(error?.response?.data?.message || tc('saveFailed')),
  });

  if (!companyId || isError) return null;

  const dirty = !!data && !sameSet(picked, data.viewers);

  return (
    <section className="rounded-md border bg-zinc-50 px-4 py-4">
      <div className="mb-1 flex items-center gap-2 text-sm font-bold">
        <DollarOutlined />
        {tc('viewersTitle')}
      </div>
      <div className="mb-3 text-[13px] text-zinc-500">{tc('viewersHint')}</div>
      <Select
        mode="multiple"
        allowClear
        showSearch
        className="w-full"
        loading={isLoading}
        options={options}
        optionFilterProp="label"
        placeholder={tc('viewersPlaceholder')}
        value={picked}
        onChange={setPicked}
        // Saved on its own, so the company form's read-only mode (which
        // disables every field under it) does not apply.
        disabled={false}
      />
      {dirty && (
        <div className="mt-3 flex justify-end gap-2">
          <Button
            shape="round"
            disabled={false}
            onClick={() => setPicked(data.viewers)}
          >
            {tc('cancel')}
          </Button>
          <Button
            shape="round"
            type="primary"
            disabled={false}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {tc('save')}
          </Button>
        </div>
      )}
    </section>
  );
}
